import { useEffect, useState } from "react";
import { Globe, CheckCircle2, Loader2, AlertCircle, ChevronDown } from "lucide-react";
import { qstLanguages, qstTranslation, qstTranslations } from "./api";

/* Language switcher for a lesson page.
 * Spanish sits at the top (client priority), then English, then more.
 * Human-reviewed translations show a green verified badge; AI-drafted
 * translations show an amber "AI draft — pending review" badge. */
export default function QstTranslationSwitcher({ slug, onContentChange }) {
  const [languages, setLanguages] = useState([]);
  const [status, setStatus] = useState({});     // lang → {human_reviewed, generated_at, reviewer}
  const [active, setActive] = useState("en");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const [L, s] = await Promise.all([qstLanguages(), qstTranslations(slug)]);
        setLanguages(L.languages || []);
        const st = {};
        for (const t of (s.translations || [])) st[t.language] = t;
        setStatus(st);
      } catch {}
    })();
  }, [slug]);

  async function switchTo(lang) {
    setOpen(false);
    if (lang === active) return;
    setActive(lang); setBusy(true); setErr(null);
    try {
      const r = await qstTranslation(slug, lang);
      setStatus((s) => ({ ...s, [lang]: r }));
      onContentChange && onContentChange(lang, r.content, {
        human_reviewed: r.human_reviewed, reviewer: r.reviewer,
      });
    } catch (e) {
      setErr("Translation unavailable — showing original.");
      setActive("en");
    } finally {
      setBusy(false);
    }
  }

  const activeLang = languages.find((l) => l.code === active);
  const activeStatus = status[active];
  const isReviewed = activeStatus?.human_reviewed;
  const isEnglish = active === "en";

  return (
    <div className="relative inline-block" data-testid="qst-language-switcher">
      <button onClick={() => setOpen(!open)}
        className="inline-flex items-center gap-2 rounded-lg border-2 border-slate-300 bg-white hover:border-[#0B2545] px-3.5 py-2 text-sm font-bold text-slate-800"
        data-testid="qst-language-btn">
        <Globe className="w-4 h-4 text-[#F97316]" />
        {activeLang?.flag} {activeLang?.label || "English"}
        {busy && <Loader2 className="w-3.5 h-3.5 animate-spin" />}
        <ChevronDown className="w-3.5 h-3.5" />
      </button>

      {open && (
        <div className="absolute right-0 top-full mt-1 z-30 w-64 bg-white border border-slate-200 rounded-xl shadow-2xl py-1.5 max-h-96 overflow-y-auto"
             data-testid="qst-language-menu">
          {languages.map((lang) => {
            const st = status[lang.code];
            const reviewed = lang.code === "en" || st?.human_reviewed;
            const cached = lang.code === "en" || !!st;
            return (
              <button key={lang.code} onClick={() => switchTo(lang.code)}
                className={`w-full flex items-center justify-between gap-2 px-3.5 py-2.5 text-sm hover:bg-slate-50 ${active === lang.code ? "bg-slate-100 font-bold" : ""}`}
                data-testid={`qst-lang-${lang.code}`}>
                <span className="flex items-center gap-2 text-slate-800">
                  <span className="text-lg">{lang.flag}</span> {lang.label}
                </span>
                {reviewed ? (
                  <span className="inline-flex items-center gap-0.5 text-[10px] font-bold text-emerald-700">
                    <CheckCircle2 className="w-3 h-3" /> Reviewed
                  </span>
                ) : cached ? (
                  <span className="inline-flex items-center gap-0.5 text-[10px] font-bold text-amber-700">
                    <AlertCircle className="w-3 h-3" /> AI draft
                  </span>
                ) : (
                  <span className="text-[10px] font-bold text-slate-400">Generate</span>
                )}
              </button>
            );
          })}
        </div>
      )}

      {!isEnglish && activeStatus && (
        <div className="absolute right-0 top-full mt-2 flex items-center gap-1.5 text-[11px]">
          {isReviewed ? (
            <span className="inline-flex items-center gap-1 rounded-full bg-emerald-100 text-emerald-800 px-2 py-0.5 font-bold uppercase tracking-widest">
              <CheckCircle2 className="w-3 h-3" /> Human Reviewed
            </span>
          ) : (
            <span className="inline-flex items-center gap-1 rounded-full bg-amber-100 text-amber-900 px-2 py-0.5 font-bold uppercase tracking-widest">
              <AlertCircle className="w-3 h-3" /> AI draft — pending review
            </span>
          )}
        </div>
      )}
      {err && <div className="absolute top-full right-0 mt-2 text-[11px] text-red-700">{err}</div>}
    </div>
  );
}
